import { CATALOG_BY_ID } from './catalogue';
import { SQ_FEET_PER_ACRE } from './units';
import { objectPolygon } from './geometry';
import { freshFilters } from './query';
import type { CanvasObject, DesignRequirement, Filters, RequirementWeight } from './types';

/** Only physical catalogue objects contribute; strokes, areas, notes and arbitrary IDs never do. */
export function deriveRequirements(objects: readonly CanvasObject[], weights: Record<string, RequirementWeight> = {}): DesignRequirement[] {
  const placed = objects.filter(object => CATALOG_BY_ID[object.kind] && Number.isFinite(object.w) && Number.isFinite(object.h) && object.w > 0 && object.h > 0);
  const requirements = new Map<string, DesignRequirement>();
  for (const object of placed) {
    const definition = CATALOG_BY_ID[object.kind]!;
    if (!definition.req) continue;
    const req = definition.req;
    const existing = requirements.get(req.key);
    if (existing) existing.sourceObjectIds.push(object.id);
    else requirements.set(req.key, { key: req.key, label: req.label, weight: weights[req.key] ?? (req.ess ? 'essential' : 'preferred'), sourceObjectIds: [object.id], ...(req.plant ? { plant: { key: req.plant, label: req.plant } } : {}) });
  }
  if (placed.length) {
    let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
    for (const item of placed) for (const point of objectPolygon(item)) {
      left = Math.min(left, point.x); right = Math.max(right, point.x);
      top = Math.min(top, point.y); bottom = Math.max(bottom, point.y);
    }
    const minAcres = (right - left) * (bottom - top) / SQ_FEET_PER_ACRE;
    requirements.set('layout-area', {
      key: 'layout-area', label: `About ${Number(minAcres.toFixed(2))} acres of usable space for this layout`,
      minAcres, weight: weights['layout-area'] ?? 'essential', sourceObjectIds: placed.map(object => object.id),
    });
  }
  return [...requirements.values()];
}
export function filtersFromRequirements(requirements: readonly DesignRequirement[], base: Partial<Filters> = {}): Filters {
  const filters = freshFilters(base);
  filters.requirements = requirements.map(requirement => ({ ...requirement, sourceObjectIds: [...requirement.sourceObjectIds] }));
  for (const requirement of requirements) filters.weights[requirement.key] = requirement.weight;
  return filters;
}
